import type { DifferencesObjectIDObject, FeatureObject, FeatureWithChanges } from "./newVersioningUtils";

/** Stable identity key for a feature, normalizing objectId to a string. */
const featureKey = (layerId: number, objectId: string | number): string => `${layerId}:${String(objectId)}`;

/**
 * Build the set of changed-feature keys ("layerId:objectId") from the version's
 * differences list (inserts + updates + deletes across all layers).
 *
 * This is the cheap, authoritative source of "what actually changed in the
 * version" — it replaces an expensive load + recursive traversal of every edit.
 */
export const buildChangedKeySet = (differences: DifferencesObjectIDObject[]): Set<string> => {
    const changedKeys = new Set<string>();
    for (const diff of differences) {
        for (const id of diff.inserts ?? []) changedKeys.add(featureKey(diff.layerId, id));
        for (const id of diff.updates ?? []) changedKeys.add(featureKey(diff.layerId, id));
        for (const id of diff.deletes ?? []) changedKeys.add(featureKey(diff.layerId, id));
    }
    return changedKeys;
};

/**
 * From the job-scoped candidate features, keep only those representing an actual
 * change: the main feature itself changed, or one of its related features
 * changed (membership in `changedKeys`). Then drop unchanged main features whose
 * related-feature set is a subset of a changed main feature's related set.
 *
 * Adapted from the original inline logic in findDifferencesForPlan; the
 * structure (two-step filter + dedup) is the same, but change membership is
 * tested against `changedKeys`
 * (the feature is itself an insert/update/delete) rather than against a
 * fully re-loaded copy of every edit in the version.
 *
 * `relationshipRowLayerIds` lists the many:many junction-table layers (Lys_IB,
 * Lys_FlytendeMerke, Farled_*, ...). A junction row is only meaningful as a related
 * feature of its main object (a light/farled). When such a row cannot be attached to
 * any of the job's mains, getAllRelatedFeaturesRecursive appends it as a standalone
 * "main"; those orphans belong to other objects changed in the version, not this job,
 * so they are excluded here even though their own key is in `changedKeys`. Junction
 * rows that did attach ride along on their main's relatedFeatures and are unaffected.
 */
/** Build the "layerId:objectId" key set for a job's directly-linked objects. */
export const buildJobObjectKeySet = (jobObjects: FeatureObject[]): Set<string> =>
    new Set(jobObjects.map((f) => featureKey(f.layerId, f.objectId)));

/**
 * Keep only candidates that belong to this job: the main feature, or any of its related
 * features, is one of the job's directly-linked objects (`jobObjectKeys`).
 *
 * Used by the rejected-plan view. Unlike filterJobFeaturesToChanged this does NOT require a
 * version-vs-DEFAULT difference, so the job's objects are shown even when nothing is pending.
 * Scoping to the job's objects is essential because a version can hold several jobs' changes:
 * the version diff drags in other jobs' deletes and many:many rows, which
 * getAllRelatedFeaturesRecursive surfaces as standalone "mains" when they can't attach to one
 * of this job's objects — those are dropped here. This job's own deletes (sector lines/areas
 * under a light) attach to that light and ride along on its relatedFeatures, so they are kept
 * and can be posted to DEFAULT.
 *
 * The related-feature check matters for combined objects: the job links e.g. an IB (layer 1),
 * but promotion makes the layer-0 light the main and demotes the IB to a related feature — so
 * the job's key is found on `relatedFeatures`, not on `mainFeature`.
 */
export const filterToJobConnectedFeatures = (
    candidates: FeatureWithChanges[],
    jobObjectKeys: Set<string>
): FeatureWithChanges[] =>
    candidates.filter(
        (c) =>
            jobObjectKeys.has(featureKey(c.mainFeature.layerId, c.mainFeature.objectId)) ||
            c.relatedFeatures.some((rf) => jobObjectKeys.has(featureKey(rf.layerId, rf.objectId)))
    );

/**
 * The full candidate filter for the plan view (findDifferencesForPlan): first scope the
 * candidates to this job's objects, then keep only those with an actual pending change.
 *
 * The job-scoping step is essential for deletes: gatherPlanCandidates seeds the candidate
 * set with every delete in the version (they can't be found via related-feature queries),
 * and a version can hold several jobs' changes. A delete that can't attach to one of this
 * job's objects surfaces as a standalone "main" — and since delete keys are always in
 * `changedKeys`, filterJobFeaturesToChanged alone would keep it, blocking the job flow with
 * a dangling unrelated delete. This job's own deletes (sectors with their lines/areas —
 * always created/updated/deleted together) attach to their job-linked light and ride along
 * on its relatedFeatures, so they survive the scoping and are posted.
 */
export const filterPlanFeatures = (
    candidates: FeatureWithChanges[],
    changedKeys: Set<string>,
    relationshipRowLayerIds: Set<number>,
    jobObjectKeys: Set<string>
): FeatureWithChanges[] =>
    filterJobFeaturesToChanged(
        filterToJobConnectedFeatures(candidates, jobObjectKeys),
        changedKeys,
        relationshipRowLayerIds
    );

export const filterJobFeaturesToChanged = (
    candidates: FeatureWithChanges[],
    changedKeys: Set<string>,
    relationshipRowLayerIds: Set<number> = new Set()
): FeatureWithChanges[] => {
    const hasChange = (feature: FeatureObject) => changedKeys.has(featureKey(feature.layerId, feature.objectId));

    // Drop candidates that are a bare relationship-row main (an unattached junction).
    const mainCandidates = candidates.filter((c) => !relationshipRowLayerIds.has(c.mainFeature.layerId));

    // Step 1: Filter to only those with direct or related changes
    const initialFiltered: FeatureWithChanges[] = [];
    const changedMainFeatureKeys = new Set<string>();
    for (const linkedFeature of mainCandidates) {
        const mainHasChange = hasChange(linkedFeature.mainFeature);
        const relatedHasChange = linkedFeature.relatedFeatures.some(hasChange);
        if (mainHasChange) {
            changedMainFeatureKeys.add(featureKey(linkedFeature.mainFeature.layerId, linkedFeature.mainFeature.objectId));
        }
        if (mainHasChange || relatedHasChange) {
            initialFiltered.push(linkedFeature);
        }
    }

    // Step 2: Remove unchanged mainFeatures whose relatedFeatures are a subset of a changed mainFeature's relatedFeatures
    const changedMainFeatures = initialFiltered.filter((f) =>
        changedMainFeatureKeys.has(featureKey(f.mainFeature.layerId, f.mainFeature.objectId))
    );
    const unchangedMainFeatures = initialFiltered.filter(
        (f) => !changedMainFeatureKeys.has(featureKey(f.mainFeature.layerId, f.mainFeature.objectId))
    );

    const getRelatedSet = (f: FeatureWithChanges) =>
        new Set(f.relatedFeatures.map((rf) => featureKey(rf.layerId, rf.objectId)));

    const changedRelatedSets = changedMainFeatures.map(getRelatedSet);

    const dedupedUnchanged = unchangedMainFeatures.filter((unchanged) => {
        const unchangedSet = getRelatedSet(unchanged);
        if (unchangedSet.size === 0) return true;
        // If any changed mainFeature contains all relatedFeatures, skip this unchanged
        return !changedRelatedSets.some((changedSet) => {
            for (const rel of unchangedSet) {
                if (!changedSet.has(rel)) return false;
            }
            return true;
        });
    });

    return [...changedMainFeatures, ...dedupedUnchanged];
};
